import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface ScoreboardFilterProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  className?: string;
}

/**
 * The one text box above a scoreboard: a name, a surname, a block, a squad —
 * whatever the desk has in its head — narrows the table as it is typed (see
 * `matchesFilter` in ../scoreboard-filter). The value lives in the URL and is
 * remembered for the session (see `useStickySearchParam`), so it survives a
 * reload, travels with a shared link, and is still there on the way back.
 */
export default function ScoreboardFilter({ value, onChange, placeholder, className }: ScoreboardFilterProps) {
  const { t } = useTranslation();

  return (
    <div className={`relative ${className ?? ''}`}>
      <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="pl-9 pr-9"
        aria-label={placeholder}
        data-testid="scoreboard-filter"
      />
      {value && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-1 top-1/2 size-7 -translate-y-1/2"
          onClick={() => onChange('')}
          aria-label={t('common.clear')}
          data-testid="scoreboard-filter-clear"
        >
          <X className="size-4" />
        </Button>
      )}
    </div>
  );
}
